/**
 * Value Transformers for Task Import
 *
 * Converts raw Excel values to backend-expected formats for each field type.
 * This is critical for proper import of non-Text/Number fields.
 *
 * Backend expects specific formats:
 * - Boolean: "1" or "0"
 * - Date: ISO format "YYYY-MM-DD"
 * - Dropdown/Plan3D: Option ID (UUID)
 * - Users: User ID (UUID)
 * - Text/Number: Pass through as-is
 */

import { TaskFieldType } from '@Share/config/enums';

/**
 * Transform raw Excel value to backend-expected format.
 *
 * @param {any} rawValue - Raw value from Excel
 * @param {number} fieldType - TaskFieldType enum value
 * @param {Array} options - Available options for Dropdown/Plan3D fields
 * @param {Object} userMappings - Email → User ID mappings (from RESOLVE_USER_EMAILS)
 * @returns {any} Transformed value ready for backend, or null if transformation fails
 */
export function transformValueForImport(rawValue, fieldType, options = [], userMappings = {}) {
  // Handle null/undefined/empty
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null;
  }

  const strValue = String(rawValue).trim();
  if (strValue === '') {
    return null;
  }

  switch (fieldType) {
    case TaskFieldType.Boolean:
      return transformBoolean(strValue);

    case TaskFieldType.Date:
      return transformDate(rawValue);

    case TaskFieldType.Dropdown:
    case TaskFieldType.Plan3D:
      return transformDropdown(strValue, options);

    case TaskFieldType.Users:
      return transformUser(strValue, userMappings);

    case TaskFieldType.Number:
      return transformNumber(rawValue);

    case TaskFieldType.Text:
    default:
      return strValue; // Text passes through
  }
}

/**
 * Transform boolean values to backend format.
 * Accepts: "Yes"/"No", "true"/"false", "1"/"0", "Y"/"N", "on"/"off"
 * Returns: "1" for truthy, "0" for falsy, null for unrecognized
 *
 * @param {string} value - String value to transform
 * @returns {string|null} "1", "0", or null
 */
function transformBoolean(value) {
  const lower = value.toLowerCase();

  const truthyValues = ['yes', 'true', '1', 'y', 'on'];
  const falsyValues = ['no', 'false', '0', 'n', 'off'];

  if (truthyValues.includes(lower)) {
    return '1';
  }

  if (falsyValues.includes(lower)) {
    return '0';
  }

  // Unrecognized value - skip field
  return null;
}

/**
 * Transform date values to ISO format (YYYY-MM-DD).
 * Handles:
 * - Excel serial date numbers (days since 1900-01-01)
 * - ISO date strings
 * - Common date formats (MM/DD/YYYY, DD/MM/YYYY, etc.)
 *
 * @param {any} value - Date value (number or string)
 * @returns {string|null} ISO date string or null
 */
function transformDate(value) {
  // Excel serial date number
  if (typeof value === 'number') {
    // Excel dates are days since 1900-01-01 (with 1900 leap year bug)
    // Formula: (serialNumber - 25569) * 86400 * 1000 = JS timestamp
    // 25569 = days from 1900-01-01 to 1970-01-01
    const date = new Date((value - 25569) * 86400 * 1000);
    if (!isNaN(date.getTime())) {
      return formatDateToISO(date);
    }
    return null;
  }

  // String date - try to parse
  const strValue = String(value).trim();

  // Already ISO format
  if (/^\d{4}-\d{2}-\d{2}/.test(strValue)) {
    const date = new Date(strValue);
    if (!isNaN(date.getTime())) {
      return formatDateToISO(date);
    }
  }

  // Try common formats: MM/DD/YYYY, DD/MM/YYYY, MM-DD-YYYY, etc.
  const date = new Date(strValue);
  if (!isNaN(date.getTime())) {
    return formatDateToISO(date);
  }

  return null;
}

/**
 * Format Date object to ISO date string (YYYY-MM-DD).
 * @param {Date} date
 * @returns {string}
 */
function formatDateToISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Transform dropdown/Plan3D text to option ID.
 * Performs case-insensitive matching against option text/value/name.
 *
 * Handles two option formats:
 * 1. Object options: { id, value/text/name, color } (from TaskBoard config)
 * 2. String options: ["Option1", "Option2"] (from auto-detection)
 *
 * @param {string} value - Display text from Excel
 * @param {Array} options - Available options array
 * @returns {string|null} Option ID or the value itself for string options, null if no match
 */
function transformDropdown(value, options) {
  if (!options || options.length === 0) {
    // No options defined - pass through the value
    // Backend may accept text values for unconfigured dropdowns
    return value;
  }

  const lowerValue = value.toLowerCase();

  // Check if options are objects or strings
  const firstOption = options[0];

  if (typeof firstOption === 'object' && firstOption !== null) {
    // Object options: { id, value/text/name, color }
    const match = options.find(opt =>
      opt.value?.toLowerCase() === lowerValue ||
      opt.text?.toLowerCase() === lowerValue ||
      opt.name?.toLowerCase() === lowerValue
    );

    if (match) {
      return match.id;
    }
  } else if (typeof firstOption === 'string') {
    // String options (from auto-detection): ["Option1", "Option2"]
    const match = options.find(opt =>
      opt.toLowerCase() === lowerValue
    );

    if (match) {
      // For string options, return the matched value itself
      // The backend will handle text-based dropdown values
      return match;
    }
  }

  // No match found - return null to skip this field
  // This follows the lenient validation approach (warn, don't fail)
  return null;
}

/**
 * Transform email to user ID using pre-resolved mappings.
 * The userMappings object is populated by RESOLVE_USER_EMAILS action.
 *
 * @param {string} email - User email from Excel
 * @param {Object} userMappings - { email: userId } lookup object
 * @returns {string|null} User ID or null if not resolved
 */
function transformUser(email, userMappings) {
  if (!userMappings || typeof userMappings !== 'object') {
    return null;
  }

  // Case-insensitive email lookup
  const lowerEmail = email.toLowerCase();

  // Direct match
  if (userMappings[lowerEmail]) {
    return userMappings[lowerEmail];
  }

  // Try original case (in case mappings use original case)
  if (userMappings[email]) {
    return userMappings[email];
  }

  // Search all keys case-insensitively
  const matchedKey = Object.keys(userMappings).find(
    key => key.toLowerCase() === lowerEmail
  );

  if (matchedKey) {
    return userMappings[matchedKey];
  }

  // Not found - skip field (lenient validation)
  return null;
}

/**
 * Transform number values - ensure numeric.
 * @param {any} value
 * @returns {number|null}
 */
function transformNumber(value) {
  const num = Number(value);
  return isNaN(num) ? null : num;
}

// Named exports for testing
export {
  transformBoolean,
  transformDate,
  transformDropdown,
  transformUser,
  transformNumber,
};

export default {
  transformValueForImport,
};
